/**
 * Avstemninger — proposals and votes in the board.  MKK module 19 «Avstemninger» (C3, 2026-10-25). Staff only; nothing
 * here is public or in the member app.
 *
 * Behind the `board_voting` capability (the menu item is absent without it, and every route answers 404).
 *   voting.vote     the votes one is eligible in: read the proposal, vote (two clicks), see the result after closing;
 *   voting.manage   «Ny avstemning» (options editor, open or secret, majority, quorum, who may vote, opening and closing
 *                   time, a board meeting item), open (two clicks), the live count in OPEN mode, who has voted, close
 *                   and cancel (two clicks).
 * The server says which in every answer and decides again on each request.
 *
 * A secret vote never shows a count while it is open, to anyone: next to «who has voted» it would tell who voted what.
 * Who voted what in an open vote is shown to the eligible voters once it is closed — never before.
 *
 * Work hub (2026-10-31): «Hvem stemmer» — the board, or (with `member_polls`) the members, in the member app: open ballots
 * only, every verified member with an account. A vote made from an agenda item («Ny avstemning» in Styremøter) carries
 * the item. The state and the result are drawn by VoteCard, as on the meeting's page.
 */
import React, { useState } from 'react';
import { errSummary, errText, fieldErrors, patch, post, send, type Body } from '../lib/api';
import { useLoad, useMsg, dropChanged } from '../lib/hooks';
import { useNav } from '../lib/nav';
import { VoteCard, STATUS_TONE, OUTCOME_TONE } from '../components/VoteCard';
import { osloTime, osloWallNow, osloWallToIso } from '../lib/format';
import { Body as CardBody, Btn, Card, Field, FieldError, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { Voting } from '../core';


type Form = { title: string; text: string; options: string[]; mode: 'open' | 'secret'; majority: 'simple' | 'two_thirds'; quorum: string;
  everyone: boolean; eligible: string[]; opensAt: string; closesAt: string; meetingItemId: string; scope: 'board' | 'members'; boardMeetingItemId: string | null };
const EMPTY: Form = { title: '', text: '', options: [...Voting.DEFAULT_OPTIONS], mode: 'open', majority: 'simple', quorum: '', everyone: true, eligible: [],
  opensAt: '', closesAt: '', meetingItemId: '', scope: 'board', boardMeetingItemId: null };
const toForm = (v: Body): Form => ({ title: v.title || '', text: v.text || '', options: [...(v.options || Voting.DEFAULT_OPTIONS)], mode: v.mode, majority: v.majority,
  quorum: v.quorumCount ? String(v.quorumCount) : '', everyone: !v.eligible || v.eligibleCount === null, eligible: (v.eligible || []).map((e: Body) => e.id),
  opensAt: v.opensAt ? osloWallNow(new Date(v.opensAt)) : '', closesAt: v.closesAt ? osloWallNow(new Date(v.closesAt)) : '', meetingItemId: v.meetingItemId || '',
  scope: v.scope === 'members' ? 'members' : 'board', boardMeetingItemId: v.boardMeetingItemId || null });
/** What is sent: the times read on the Oslo wall, empty fields as null, «alle» as eligible null. */
export function voteBody(f: Form): Body {
  const members = f.scope === 'members';
  return { title: f.title, text: f.text.trim() || null, options: f.options.map((o) => o.trim()), mode: members ? 'open' : f.mode, majority: f.majority,
    quorumCount: f.quorum.trim() ? Number(f.quorum) : null, eligible: f.everyone || members ? null : f.eligible,
    opensAt: osloWallToIso(f.opensAt), closesAt: osloWallToIso(f.closesAt), meetingItemId: f.meetingItemId.trim() || null,
    scope: f.scope, boardMeetingItemId: f.boardMeetingItemId };
}
/** «For 5 · Mot 2 · Avstår 1». */
export const countsLine = (options: string[], counts: number[]) => Voting.countsText(options, counts);

function VoteForm({ f, set, errors, voters, pollsOn }: { f: Form; set: (f: Form) => void; errors: Record<string, string>; voters: Body[]; pollsOn: boolean }) {
  // FieldError: red under the field and a toast (2026-10-31).
  const err = (k: string) => <FieldError text={errors[k]} />;
  const opt = (i: number, v: string) => set({ ...f, options: f.options.map((o, j) => (j === i ? v : o)) });
  const members = f.scope === 'members';
  return (
    <>
      {(pollsOn || members) && (
        <fieldset className="field">
          <legend>Hvem stemmer</legend>
          <label><input type="radio" name="vote-scope" checked={!members} onChange={() => set({ ...f, scope: 'board' })} /> {Voting.SCOPE_NO.board} — de stemmeberettigede i panelet og i appen for ansatte</label>
          <label><input type="radio" name="vote-scope" checked={members} disabled={!pollsOn} onChange={() => set({ ...f, scope: 'members', mode: 'open', everyone: true })} /> {Voting.SCOPE_NO.members} — i medlemsappen, alle bekreftede medlemmer med konto (åpen avstemning)</label>
          {err('scope')}
        </fieldset>
      )}
      {f.boardMeetingItemId && <p className="note">Avstemningen gjelder en sak i et styremøte.</p>}
      <div className="row">
        <Field label="Tittel" required className="field">{(id) => <><input id={id} maxLength={Voting.MAX_TITLE} autoComplete="off" value={f.title}
          onChange={(e) => set({ ...f, title: e.target.value })} />{err('title')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Forslaget" className="field" note="Det de stemmeberettigede leser før de stemmer.">{(id, d) => <><textarea id={id} aria-describedby={d} rows={5}
          maxLength={Voting.MAX_TEXT} value={f.text} onChange={(e) => set({ ...f, text: e.target.value })} />{err('text')}</>}</Field>
      </div>
      <fieldset className="field">
        <legend>Alternativer</legend>
        <p className="note">Det første alternativet er forslaget. «Avstår» telles med i beslutningsdyktigheten, men ikke i flertallet. {Voting.MIN_OPTIONS}–{Voting.MAX_OPTIONS} alternativer.</p>
        {f.options.map((o, i) => (
          <div className="row" key={i} style={{ alignItems: 'center', gap: 8 }}>
            <input aria-label={`Alternativ ${i + 1}`} maxLength={Voting.MAX_OPTION} value={o} onChange={(e) => opt(i, e.target.value)} />
            {f.options.length > Voting.MIN_OPTIONS && <Btn ghost aria-label={`Fjern alternativ ${i + 1}`} onClick={() => set({ ...f, options: f.options.filter((_, j) => j !== i) })}>Fjern</Btn>}
          </div>
        ))}
        {f.options.length < Voting.MAX_OPTIONS && <Btn ghost onClick={() => set({ ...f, options: [...f.options, ''] })}>+ Legg til alternativ</Btn>}
        {err('options')}
      </fieldset>
      <div className="row">
        {!members && <fieldset className="field half">
          <legend>Avstemning</legend>
          <label><input type="radio" name="vote-mode" checked={f.mode === 'open'} onChange={() => set({ ...f, mode: 'open' })} /> Åpen — etter avslutning ser de stemmeberettigede hvem som stemte hva</label>
          <label><input type="radio" name="vote-mode" checked={f.mode === 'secret'} onChange={() => set({ ...f, mode: 'secret' })} /> Hemmelig — bare antall stemmer lagres per alternativ</label>
        </fieldset>}
        <Field label="Flertall" className="field half">{(id) => <select id={id} value={f.majority} onChange={(e) => set({ ...f, majority: e.target.value as Form['majority'] })}>
          <option value="simple">{Voting.MAJORITY_NO.simple} (flere enn halvparten)</option>
          <option value="two_thirds">{Voting.MAJORITY_NO.two_thirds}</option></select>}</Field>
      </div>
      <div className="row">
        <Field label="Beslutningsdyktig" className="field half" note="Minst så mange må stemme (valgfritt).">{(id, d) => <><input id={id} aria-describedby={d} type="number" min={1} max={200}
          inputMode="numeric" value={f.quorum} onChange={(e) => set({ ...f, quorum: e.target.value })} />{err('quorumCount')}</>}</Field>
        <Field label="Sak i styremøte" className="field half" note="Valgfritt: sakens id fra styremøtet.">{(id, d) => <><input id={id} aria-describedby={d} maxLength={Voting.MAX_MEETING_ITEM}
          autoComplete="off" value={f.meetingItemId} onChange={(e) => set({ ...f, meetingItemId: e.target.value })} />{err('meetingItemId')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Åpner (valgfritt)" className="field half" note="Tomt: du åpner den selv.">{(id, d) => <><input id={id} aria-describedby={d} type="datetime-local" value={f.opensAt}
          onChange={(e) => set({ ...f, opensAt: e.target.value })} />{err('opensAt')}</>}</Field>
        <Field label="Stenger (valgfritt)" className="field half" note="Tomt: du avslutter den selv.">{(id, d) => <><input id={id} aria-describedby={d} type="datetime-local" value={f.closesAt}
          onChange={(e) => set({ ...f, closesAt: e.target.value })} />{err('closesAt')}</>}</Field>
      </div>
      {!members && <fieldset className="field">
        <legend>Hvem kan stemme</legend>
        <label><input type="checkbox" checked={f.everyone} onChange={(e) => set({ ...f, everyone: e.target.checked })} /> Alle med stemmerett når avstemningen åpner ({voters.length})</label>
        {!f.everyone && voters.map((p) => (
          <label key={p.id} style={{ display: 'block' }}><input type="checkbox" checked={f.eligible.includes(p.id)}
            onChange={(e) => set({ ...f, eligible: e.target.checked ? [...f.eligible, p.id] : f.eligible.filter((x) => x !== p.id) })} /> {p.name}</label>
        ))}
        <p className="note">Listen låses når avstemningen åpner. Minst {Voting.MIN_ELIGIBLE} må kunne stemme.</p>
        {err('eligible')}
      </fieldset>}
      {members && <p className="note">Medlemmene ser avstemningen på Hjem i appen. Listen over hvem som kan stemme låses når den åpner. Hva hvert medlem stemte, ser bare dere som styrer avstemningen — aldri andre medlemmer.</p>}
    </>
  );
}

function Detail({ id, onChanged, onEdit }: { id: string; onChanged: () => void; onEdit: (v: Body) => void }) {
  const { data, error, reload } = useLoad<Body>(`/voting/votes/${id}`, [id]);
  const [msg, say] = useMsg();
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const v = data.vote, manage = Boolean(data.can && data.can.manage);
  const act = async (what: string, done: string) => {
    const r = await post(`/voting/votes/${v.id}/${what}`);
    say(r.ok ? done : errText(r.body), r.ok ? 'ok' : 'err'); reload(); onChanged();
  };
  const remove = async () => {
    const r = await send(`/voting/votes/${v.id}`, { method: 'DELETE' });
    say(r.ok ? 'Utkastet er slettet.' : errText(r.body), r.ok ? 'ok' : 'err'); onChanged();
  };
  const cast = async (i: number) => {
    const r = await post(`/voting/votes/${v.id}/ballot`, { option: i });
    say(r.ok ? 'Du har stemt.' : errText(r.body), r.ok ? 'ok' : 'err'); reload(); onChanged();
  };
  const res = v.result;
  return (
    <Card title={v.title} hint={<>{v.scope === 'members' ? `${Voting.SCOPE_NO.members} · ` : ''}{Voting.MODE_NO[v.mode]} · {Voting.MAJORITY_NO[v.majority]}{v.quorumCount ? ` · minst ${v.quorumCount} må stemme` : ''}
      {v.meetingItemId ? ` · sak ${v.meetingItemId}` : ''}</>}>
      <CardBody>
        <p><Tag tone={STATUS_TONE[v.status]}>{Voting.STATUS_NO[v.status]}</Tag>{' '}
          {v.status === 'open' && v.closesAt && <>Stenger {osloTime(v.closesAt)}. </>}
          {v.status === 'draft' && v.opensAt && <>Åpner {osloTime(v.opensAt)}. </>}
          {v.status !== 'draft' && <>{v.votedCount} av {v.eligibleCount} har stemt.</>}</p>
        {v.text && <p style={{ whiteSpace: 'pre-line' }}>{v.text}</p>}
        <p className="note">{v.rule}</p>
        <Msg text={msg.text} tone={msg.tone} />

        {/* ---- voting ---- */}
        {v.me.voted && <p><Tag tone="green">Du har stemt</Tag> {osloTime(v.me.votedAt)} · kvittering {v.me.receipt}{v.mode === 'secret' ? ' · hemmelig: ingen kan se hva du stemte' : ''}</p>}
        {v.me.canVote && (
          <div className="row" role="group" aria-label="Stem" style={{ gap: 8, flexWrap: 'wrap' }}>
            {v.options.map((o: string, i: number) => (
              <TwoStep key={i} label={o} ghost={false} go confirmLabel={`Klikk igjen: stem «${o}»`} busyLabel="Stemmer …" onConfirm={() => cast(i)} />
            ))}
          </div>
        )}

        {/* ---- the live count (open mode, managers) and who has voted ---- */}
        {v.live && <p><b>Foreløpig:</b> {countsLine(v.options, v.live)}</p>}
        {v.status === 'open' && v.mode === 'secret' && manage && <p className="note">Hemmelig avstemning: ingen tall vises før den er avsluttet.</p>}
        {manage && v.eligible && v.status !== 'draft' && v.scope !== 'members' && (
          <table className="t">
            <thead><tr><th scope="col">Stemmeberettiget</th><th scope="col">Har stemt</th></tr></thead>
            <tbody>{v.eligible.map((e: Body) => <tr key={e.id}><td>{e.name || '—'}</td><td>{e.voted ? 'Ja' : 'Nei'}</td></tr>)}</tbody>
          </table>
        )}
        {manage && v.status === 'draft' && <p className="note">{v.eligibleCount !== null ? `${v.eligible.length} valgt: ${v.eligible.map((e: Body) => e.name).join(', ')}` : 'Alle med stemmerett når den åpner.'}</p>}

        {/* ---- the result (VoteCard, as on the meeting's page) ---- */}
        {res && (
          <>
            <VoteCard vote={v} />
            {v.ballots && (
              <table className="t">
                <thead><tr><th scope="col">Hvem</th><th scope="col">Stemte</th></tr></thead>
                <tbody>{v.ballots.map((b: Body) => <tr key={b.staffId || b.memberId}><td>{b.name || '—'}</td><td>{v.options[b.option]}</td></tr>)}</tbody>
              </table>
            )}
            {v.mode === 'secret' && <p className="note">Hemmelig avstemning: bare antall stemmer per alternativ er lagret.</p>}
          </>
        )}

        {/* ---- the manager's buttons ---- */}
        {manage && (
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {v.status === 'draft' && <Btn ghost onClick={() => onEdit(v)}>Endre</Btn>}
            {v.status === 'draft' && <TwoStep label="Åpne avstemningen" ghost={false} go busyLabel="Åpner …"
              confirmLabel="Klikk igjen: den åpnes, og listen over hvem som kan stemme låses" onConfirm={() => act('open', 'Avstemningen er åpnet.')} />}
            {v.status === 'draft' && <TwoStep label="Slett utkastet" busyLabel="Sletter …" confirmLabel="Klikk igjen: utkastet slettes" onConfirm={remove} />}
            {v.status === 'open' && <TwoStep label="Avslutt nå" ghost={false} go busyLabel="Avslutter …"
              confirmLabel="Klikk igjen: ingen kan stemme mer, og stemmene telles" onConfirm={() => act('close', 'Avstemningen er avsluttet og telt.')} />}
            {['draft', 'open'].includes(v.status) && <TwoStep label="Avlys" busyLabel="Avlyser …"
              confirmLabel={v.status === 'open' ? 'Klikk igjen: avlyses, og stemmene som er avgitt slettes' : 'Klikk igjen: avlyses'} onConfirm={() => act('cancel', 'Avstemningen er avlyst.')} />}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export function Avstemninger() {
  const { data, error, reload } = useLoad<Body>('/voting/votes');
  const { caps } = useNav();
  const pollsOn = Boolean(caps && caps.includes('member_polls'));
  const manage = Boolean(data && data.can && data.can.manage);
  const voters = useLoad<Body>(manage ? '/voting/voters' : null, [manage]);
  const [msg, say] = useMsg();
  const [open, setOpen] = useState<string | null>(null);
  const [form, setForm] = useState<{ id: string | null; f: Form; start: Form } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [rev, setRev] = useState(0);

  const edit = (id: string | null, f: Form) => { setErrors({}); say(''); setForm({ id, f, start: f }); };
  const save = async () => {
    if (!form || saving) return;
    if (form.f.opensAt && !osloWallToIso(form.f.opensAt)) { setErrors({ opensAt: 'Velg en dato og et klokkeslett.' }); return; }
    if (form.f.closesAt && !osloWallToIso(form.f.closesAt)) { setErrors({ closesAt: 'Velg en dato og et klokkeslett.' }); return; }
    setSaving(true);
    try {
      const r = form.id ? await patch(`/voting/votes/${form.id}`, voteBody(form.f)) : await post('/voting/votes', voteBody(form.f));
      if (!r.ok) { setErrors(fieldErrors(r.body)); return say(errSummary(r.body), 'err'); }
      setForm(null); say(form.id ? 'Utkastet er lagret.' : 'Utkastet er laget. Åpne det når det er klart.', 'ok');
      setOpen(r.body.vote.id); setRev((x) => x + 1); reload();
    } finally { setSaving(false); }
  };

  const items: Body[] = (data && data.items) || [];
  return (
    <>
      <Card title="Avstemninger" hint="Forslag i styret: åpen eller hemmelig avstemning, flertall og beslutningsdyktighet, og opptellingen. Bare for ansatte med stemmerett; ingenting vises for medlemmer.">
        {manage && <div className="work-bar"><Btn small={false} onClick={() => edit(null, EMPTY)}>+ Ny avstemning</Btn></div>}
        {!form && <CardBody><Msg text={msg.text} tone={msg.tone} /></CardBody>}
        {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
          <table className="t">
            <thead><tr><th scope="col">Forslag</th><th scope="col">Status</th><th scope="col">Avstemning</th><th scope="col">Stenger</th><th scope="col">Stemt</th>
              <th scope="col"><span className="vh">Handling</span></th></tr></thead>
            <tbody>{items.length ? items.map((v) => (
              <tr key={v.id}>
                <td><b>{v.title}</b>{v.meetingItemId && <div className="sub">Sak {v.meetingItemId}</div>}{v.boardMeetingItemId && <div className="sub">Sak i styremøte</div>}
                  {v.me.canVote && <div className="sub"><Tag tone="gold">Venter på din stemme</Tag></div>}</td>
                <td><Tag tone={STATUS_TONE[v.status]}>{Voting.STATUS_NO[v.status]}</Tag>{v.result && <> <Tag tone={OUTCOME_TONE[v.result.outcome]}>{Voting.OUTCOME_NO[v.result.outcome]}</Tag></>}</td>
                <td>{v.scope === 'members' ? Voting.SCOPE_NO.members : v.mode === 'secret' ? 'Hemmelig' : 'Åpen'}</td>
                <td>{v.closesAt ? osloTime(v.closesAt) : '—'}</td>
                <td>{v.status === 'draft' ? '—' : `${v.votedCount} av ${v.eligibleCount}`}</td>
                <td><Btn ghost aria-label={`Vis ${v.title}`} onClick={() => setOpen(v.id)}>{v.me.canVote ? 'Stem' : 'Vis'}</Btn></td>
              </tr>)) : <tr><td colSpan={6}>{manage ? 'Ingen avstemninger ennå. Lag den første med «Ny avstemning».' : 'Ingen avstemninger der du har stemmerett.'}</td></tr>}</tbody>
          </table>
        )}
      </Card>
      {open && <Detail key={`${open}:${rev}`} id={open} onChanged={() => { reload(); }} onEdit={(v) => edit(v.id, toForm(v))} />}
      <FormDialog open={Boolean(form)} title={form && form.id ? 'Endre utkast' : 'Ny avstemning'} wide
        dirty={Boolean(form) && JSON.stringify(form!.f) !== JSON.stringify(form!.start)} onClose={() => setForm(null)}>
        {form && <>
          <VoteForm f={form.f} set={(f) => { setErrors(dropChanged(errors, form.f, f)); setForm({ ...form, f }); }} errors={errors} voters={(voters.data && voters.data.items) || []} pollsOn={pollsOn} />
          <div className="row"><Btn small={false} disabled={saving} onClick={save}>{saving ? 'Lagrer …' : form.id ? 'Lagre' : 'Lagre som utkast'}</Btn></div>
          <Msg text={msg.text} tone={msg.tone} />
        </>}
      </FormDialog>
    </>
  );
}
